import { ChangeEvent, useEffect, useState } from 'react';
import { Search } from 'react-feather';
import { useHistory } from 'react-router-dom';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import { AddDiscountMutation as AddDiscount } from '__generated__/graphql';
import { isEmpty } from '../../../../common/helpers';
import AddDiscountForm from './AddDiscountForm';
import DiscountList from './DiscountList';
import GetDiscountQuery from './GetDiscountQuery';
import GetDiscountsQuery from './GetDiscountsQuery';
import UI from '../../../../common/UI';
import useProject from '../../../useProject';
import useQuery from '../../../../api/useQuery';
import useUrlState from '../../../../common/useUrlState';

type AddedDiscount = AddDiscount['addDiscount'];

interface DiscountsPageInterface {
  eventId: string;
  discountId?: string;
}

interface SearchPaginationParams {
  search: string | null;
}

const DiscountsPage = ({ eventId, discountId }: DiscountsPageInterface) => {
  const { t } = useTranslation();
  const history = useHistory();
  const project = useProject();

  const [filtersKey, setFiltersKey] = useState(0);

  const [urlState, setUrlState] = useUrlState<SearchPaginationParams>({
    parse: (rawParams, { string }) => ({
      search: string(rawParams.search),
    }),
    onChange: () => {
      // Rerender the filters when user clicks on 'Registrations' in the nav bar.
      setFiltersKey((key) => key + 1);
    },
  });

  const { data, refetch, loading } = useQuery(
    GetDiscountsQuery,
    {
      variables: {
        ...urlState,
        project_id: project.id,
        event_id: eventId,
      },
    },
  );

  const event = data?.project.event;

  const { data: discountData, loading: discountLoading } = useQuery(
    GetDiscountQuery,
    {
      variables: {
        event_id: eventId,
        discount_id: discountId,
      },
      skip: !discountId,
    },
  );

  const [openAddDiscountModal, closeAddDiscountModal] = useModal(() => {
    const handleSuccess = (discount: AddedDiscount) => {
      closeAddDiscountModal();
      history.push(`/${project.id}/events/${event.id}/discounts/${discount.id}`);
    };

    return (
      <UI.Modal maxWidth={600} close={closeAddDiscountModal}>
        <AddDiscountForm
          onSuccess={handleSuccess}
          event={event}
        />
      </UI.Modal>
    );
  }, [event]);

  useEffect(() => {
    refetch();
  }, [discountId, refetch]);

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    // Search input is debounced so we can update params immediately
    setUrlState((oldParams) => ({ ...oldParams, search: event.target.value || null }), true);
  };

  // Filter helpers
  const hasFilters = Object.values(urlState).some((param) => !isEmpty(param));

  const resetFilters = () => {
    setUrlState({ search: null });
  };

  // Either we have discounts, or we have no discounts when filters are enabled
  const hasDiscounts = event?.discounts.length > 0 || (event?.discounts.length === 0 && hasFilters);

  if (!event) {
    return <UI.PageLoader />;
  }

  return (
    <UI.Section>
      <UI.GridContainer>
        <UI.FlexContainer sc={{ justifyContent: 'space-between', direction: ['column', 'row'] }}>
          <UI.SectionHeading>
            {t('discounts')}
          </UI.SectionHeading>

          {hasDiscounts && (
            <UI.FlexContainer sc={{ spaceX: 1 }}>
              <UI.Button
                sc={{ brand: 'secondary' }}
                onClick={() => openAddDiscountModal()}
              >
                {t('add_discount')}
              </UI.Button>
            </UI.FlexContainer>
          )}
        </UI.FlexContainer>

        <UI.GridContainer sc={{ columns: ['1fr', '1fr 3fr'] }}>
          <UI.Div>
            <FilterBar
              onSearch={handleSearch}
              search={urlState.search}
              key={filtersKey}
            />
          </UI.Div>

          <UI.Div>
            {!hasDiscounts && (
              <UI.Card sc={{ padding: 8, textAlign: 'center', background: 'gray.50' }}>
                <UI.H3 sc={{ mb: 2 }}>{t('add_discounts')}</UI.H3>
                <UI.Paragraph>{t('discounts_get_started')}</UI.Paragraph>
                <UI.Button
                  onClick={() => openAddDiscountModal()}
                  sc={{ brand: 'secondary' }}
                >
                  {t('add_a_discount')}
                </UI.Button>
              </UI.Card>
            )}
            {hasDiscounts && (
              <UI.Div sc={{ loading }}>
                <DiscountList
                  event={event}
                  onResetFilters={() => resetFilters()}
                  activeDiscountId={discountId}
                  // Always pass a fresh data object
                  discountData={discountData?.discount.id === discountId && !discountLoading ? discountData : null}
                />
              </UI.Div>
            )}
          </UI.Div>
        </UI.GridContainer>
      </UI.GridContainer>
    </UI.Section>
  );
};

interface FilterBarProps {
  search: string;
  onSearch: (event: ChangeEvent<HTMLInputElement>) => void;
}

const FilterBar = ({ search, onSearch }: FilterBarProps) => {
  const { t } = useTranslation();

  return (
    <UI.InputGroup sc={{ mb: 0 }}>
      <UI.InputWrapper>
        <span>
          <UI.Icon>
            <Search />
          </UI.Icon>
        </span>
        <UI.DebouncedInput
          type="text"
          value={search}
          onChange={onSearch}
          placeholder={t('search_discounts')}
          sc={{ pl: 5 }}
        />
      </UI.InputWrapper>
    </UI.InputGroup>
  );
};

export default DiscountsPage;
